import { useState, useCallback } from 'react';
import { ThreeDotsVertical } from 'react-bootstrap-icons';

import { Button, Dropdown, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';

import BaseCard from '@/components/shared/BaseCard';
import { aspectRatioPadding } from '@/components/shared/ImageRatio';
import NotesProps from '@/interfaces/notes';
import styled, { StyledProp, css } from '@/styled';

import { useNotes } from '../context';
import UnmarkConfirmation from '../UnmarkConfirmation';

interface NotesCardProps extends StyledProp, NotesProps {}

const NotesCard = (data: NotesCardProps) => {
  const { setCurrentNote } = useNotes();
  const { lesson, course, image, className } = data;

  const [openUnmarkConfirmation, setOpenUnmarkConfirmation] = useState(false);
  const toggleUnmarkModal = () => setOpenUnmarkConfirmation(isOpened => !isOpened);

  const handleClick = useCallback(() => setCurrentNote(data), [data, setCurrentNote]);

  const items: MenuProps['items'] = [
    {
      key: 'view',
      label: 'Ver anotação',
      onClick: handleClick
    },
    {
      key: 'delete',
      label: 'Excluir anotação',
      onClick: toggleUnmarkModal
    }
  ];

  return (
    <BaseCard
      className={className}
      internalSpace={16}
      image={{ src: image, aspect: '16:9' }}
      extraContent={
        <div className='note-card-actions'>
          <Space className='note-card-actionsContent' direction='vertical' size={16}>
            <Button block onClick={handleClick}>
              Ver anotação
            </Button>

            <Button block danger onClick={toggleUnmarkModal}>
              Excluir anotação
            </Button>
          </Space>
        </div>
      }
    >
      <UnmarkConfirmation noteId={lesson.id} open={openUnmarkConfirmation} onCancel={toggleUnmarkModal} />

      <div className='note-card-data'>
        <Typography.Text strong onClick={handleClick} ellipsis>
          {lesson?.title ?? 'Título da aula'}
        </Typography.Text>

        <div className='note-card-data-mobileActions'>
          <Dropdown menu={{ items }} trigger={['click']} placement='bottomRight'>
            <Button size='small' type='ghost' shape='circle'>
              <ThreeDotsVertical />
            </Button>
          </Dropdown>
        </div>
      </div>

      <Typography.Text type='secondary' ellipsis>
        {course?.title ?? 'Nome do curso'}
      </Typography.Text>
    </BaseCard>
  );
};

export default styled(NotesCard)`
  ${({ theme }) => {
    return css`
      ${theme.breakpoints.up('lg')} {
        &:hover {
          .note-card-actions {
            opacity: 1;
            pointer-events: all;
          }
        }
      }

      .note-card-data {
        display: flex;

        ${theme.breakpoints.down('lg')} {
          justify-content: space-between;
        }

        .note-card-data-mobileActions {
          display: none;

          ${theme.breakpoints.down('lg')} {
            display: flex;
          }
        }
      }

      .note-card-actions {
        transition: ${theme.motionDurationSlow};
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 2;
        background-color: ${theme.palette.alpha('#000000', 0.9)};
        height: 0;
        padding-top: ${aspectRatioPadding['16:9']};
        overflow: hidden;
        border-radius: ${theme.borderRadius}px;

        .note-card-actionsContent {
          display: flex;
          flex-direction: column;
          padding: ${theme.space.spacing(4, 12)};
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ant-space-item {
          width: 100%;
        }

        button,
        a {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 40px;
          background: ${theme.palette.gray[9]};
          border-color: ${theme.palette.gray[7]};
          color: ${theme.palette.gray[0]};
        }
      }
    `;
  }}
`;
